import {Grid, Typography} from '@mui/material';
import {MultiFileUpload} from 'components/form/dropzone';
import {DropzoneType} from 'configs/themeConfig';
import React, {useEffect, useRef} from 'react';
import {useController} from 'react-hook-form';
import {CustomFile} from 'types';
import {FieldsRendererProps} from '.';
import {getFileIconHelper, getMimeTypeHelper, SelectOptionsType} from 'global';
import {useDeleteMediaFilesByIds, useCreateMediaFiles} from 'hooks';
import {MediaFileModel, MediaFileObjectTypeEnum} from 'models';

interface RenderUploadFileProps extends FieldsRendererProps {
    gridSize?: number;
}

export const RenderUploadFile: React.FC<RenderUploadFileProps> = ({
    field,
    control,
    gridSize,
    setValue,
    itemId,
    indexKey,
    watch,
}) => {
    const {
        field: {value, onChange},
        fieldState: {error, isTouched},
    } = useController({
        name: `data.${indexKey}.files`,
        control,
        rules: {
            required: field.isRequired ? 'This field is required' : false,
        },
        defaultValue: [],
    });
    const prevFiles = useRef<CustomFile[]>([]);
    const {deleteMediaFilesByIds, deleteMediaFilesByIdsLoading, deleteMediaFilesByIdsResponse} =
        useDeleteMediaFilesByIds();
    const {
        createMedieFile: storeMediaFiles,
        createMedieFileLoading: storeMediaFilesLoading,
        createMedieFileResponse: storeMediaFilesResponse,
    } = useCreateMediaFiles();
    const mappedFiles = watch(`data.${indexKey}.files`);
    const fieldValue: SelectOptionsType[] = watch(`data.${indexKey}.value.multiSelectedOptions`) ?? [];

    useEffect(() => {
        if (!fieldValue || fieldValue.length === 0) return;
        if (fieldValue.length > 0) {
            const urls = fieldValue.map(file => {
                const x = file.value;
                const extension = x.split('.').pop()?.toLowerCase();
                const mimeType = getMimeTypeHelper(extension);
                const isImage = mimeType.startsWith('image/');
                const previewUrl = isImage
                    ? `${process.env.REACT_APP_API_BASE_URL}/${x}`
                    : getFileIconHelper(extension);

                return {
                    name: file.label,
                    path: x,
                    preview: previewUrl,
                    type: mimeType,
                    lastModified: Date.now(),
                    lastModifiedDate: new Date(),
                    webkitRelativePath: '',
                } as CustomFile;
            });
            setValue(`data.${indexKey}.files`, urls);
            prevFiles.current = urls;
        } else {
            setValue(`data.${indexKey}.files`, []);
            prevFiles.current = [];
        }
    }, []);

    useEffect(() => {
        if (mappedFiles && field.value && itemId) {
            const storeFiles = mappedFiles.filter((x: CustomFile) => x.preview?.includes('blob:'));
            const mappedPaths = new Set(mappedFiles.map((file: CustomFile) => file.path));

            const removedIds = prevFiles.current
                .filter(file => !mappedPaths.has(file.path))
                .map(file => (!!file.name ? file.name?.split('.')?.[0] : ''));

            if (storeFiles.length > 0) {
                storeMediaFiles({
                    variables: {
                        files: storeFiles,
                        objectId: itemId,
                        objectType: MediaFileObjectTypeEnum.ITEM_DATA,
                    },
                });
            }
            if (removedIds.length > 0) {
                deleteMediaFilesByIds({
                    variables: {
                        ids: removedIds,
                    },
                });
            }
        }
    }, [mappedFiles]);

    useEffect(() => {
        if (!deleteMediaFilesByIdsLoading && deleteMediaFilesByIdsResponse) {
            const newValues = mappedFiles.map((file: CustomFile) => ({
                label: file.name,
                value: file.path,
            })) as SelectOptionsType[];

            setValue(`data.${indexKey}.value.multiSelectedOptions`, [...newValues]);
        }
    }, [deleteMediaFilesByIdsResponse, deleteMediaFilesByIdsLoading]);

    useEffect(() => {
        if (!storeMediaFilesLoading && storeMediaFilesResponse) {
            const newFiles = storeMediaFilesResponse.map((file: MediaFileModel) => {
                const x = file.fileName;
                const extension = x.split('.').pop()?.toLowerCase();
                const mimeType = getMimeTypeHelper(extension);
                const isImage = mimeType.startsWith('image/');
                const previewUrl = isImage
                    ? `${process.env.REACT_APP_API_BASE_URL}/${file.partialFilePath}`
                    : getFileIconHelper(extension);

                return {
                    name: file.fileName,
                    path: x,
                    preview: previewUrl,
                    type: mimeType,
                    lastModified: Date.now(),
                    lastModifiedDate: new Date(),
                    webkitRelativePath: '',
                } as CustomFile;
            });
            const responseValues = storeMediaFilesResponse.map(
                x => ({label: x.fileName, value: x.partialFilePath}) as SelectOptionsType,
            );
            const previousFiles = mappedFiles.filter((x: CustomFile) => !x.preview?.includes('blob:'));
            setValue(`data.${indexKey}.value.multiSelectedOptions`, [
                ...watch(`data.${indexKey}.value.multiSelectedOptions`),
                ...responseValues,
            ]);
            setValue(`data.${indexKey}.files`, [...previousFiles, ...newFiles]);
            prevFiles.current = [...prevFiles.current, ...newFiles];
        }
    }, [storeMediaFilesResponse, storeMediaFilesLoading]);

    const handleSetFiles = (_: string, files: CustomFile[]) => {
        onChange(files);
    };

    return (
        <Grid item xs={gridSize}>
            <Typography variant="body2" style={{marginBottom: '5px'}}>
                {field.title} {field.isRequired && <span style={{color: 'red'}}>*</span>}
            </Typography>
            <MultiFileUpload
                type={DropzoneType.DEFAULT}
                showList={!!value?.length}
                previewImageSize={80}
                setFieldValue={handleSetFiles}
                files={value || []}
                error={isTouched && !!error}
            />
        </Grid>
    );
};

export default RenderUploadFile;
